/*
 * Orders derivations — canon §7.1 (order card) + §7.2 (seats), transcribed
 * from the App-shell canon and its DC source (App Shell.dc.html ordersView /
 * seat()). Pure functions over the SHIPPED domain types (types.ts Ticket with
 * ruling-B seats[]) — the canon [demo] dataset (src/data/seed.ts) exercises
 * them ONLY in tests (ruling A).
 *
 * SOURCE OF TRUTH: the my-api holder is authoritative for allocation state
 * (PRD-ONEPULSE-PARTICIPANT-CUSTOMER-FLOW-V1; fix e75108d). applyAllocations
 * maps holders onto the ruling-B seats[]:
 *   no holder → unassigned · invited → pending · claimed/ready/consumed →
 *   assigned (mine when the allocated email is the signed-in account's).
 *
 * STATE-COLOUR SEMANTICS (canon §7.1/§7.2 [token], boardroom ruling): amber
 * `--warn` = seat(s) unassigned / needs action; blue `--info-invited`
 * (#8ab4f8) = invite pending; teal `--accent` = done/assigned. Never collapse
 * mixed per-seat states into one order-level status (§7.2 must-not).
 */
import type { OrderAllocations } from '../../data/myApi'
import type { Seat, Ticket } from '../../data/types'

// ---------------------------------------------------------------------------
// Allocation merge (live my-api holders → ruling-B seats[])
// ---------------------------------------------------------------------------

/** Merge my-api allocation holders into a ticket's seats[].
 *
 *  Today's my-api is 1-ticket = 1-seat, so the (single) holder lands on seat 1
 *  and every ticket degrades to seats.length === 1; when the seats[] wire
 *  contract lands (TODO(infra seats[] contract, ruling B) in types.ts) the
 *  holder set becomes per-seat and this merge is already shaped for it. */
export function applyAllocations(
  tickets: Ticket[],
  allocations: OrderAllocations | null,
  sessionEmail: string | undefined,
): Ticket[] {
  if (!allocations) {
    // my-api not readable — allocation state is UNKNOWN. Nothing is invented:
    // seats render unassigned with actions disabled behind the honest service
    // notice (never box-office seatStatus, which is not allocation-authoritative).
    return tickets.map((t) => ({
      ...t,
      seats: t.seats.map((s) => ({ seatNo: s.seatNo, status: 'unassigned' as const })),
    }))
  }
  const me = (sessionEmail ?? '').trim().toLowerCase()
  return tickets.map((t) => {
    const holder = allocations.tickets.find((x) => x.ticketId === t.id)?.holder ?? null
    const seats: Seat[] = t.seats.map((s) => {
      if (s.seatNo !== 1 || !holder || holder.state === 'revoked') {
        return { seatNo: s.seatNo, status: 'unassigned' }
      }
      const email = holder.allocatedEmail ?? undefined
      const mine = !!me && !!email && email.trim().toLowerCase() === me
      return holder.state === 'invited'
        ? { seatNo: s.seatNo, status: 'pending', holder: { email } }
        : { seatNo: s.seatNo, status: 'assigned', holder: { email }, mine }
    })
    return { ...t, seats }
  })
}

// ---------------------------------------------------------------------------
// Per-ticket derivations (canon §7.1 ticket row / §7.2 status pill)
// ---------------------------------------------------------------------------

/** Display bucket for a seat: checked_in is terminal-assigned (per-ticket
 *  state machine Unassigned → Invited·pending → Assigned → Checked-in). */
const seatFilled = (s: Seat) => s.status !== 'unassigned'

/** Holder display line: name, else email (never fabricated). */
export function holderLabel(s: Seat): string {
  return s.holder?.name || s.holder?.email || ''
}

export type TicketDotState = 'need' | 'pending' | 'done'

export interface TicketAssignment {
  assigned: number
  seats: number
  done: boolean
  pending: boolean
  mine: boolean
  /** Dot / accent state: amber need · blue pending · teal done (§7.1 [token]). */
  dot: TicketDotState
  /** Sub-line per the canon templates (§7.1): needs → `{n} to assign` /
   *  `{n} of {m} still to assign`; done → `You` / holder / `{n} people
   *  assigned`; pending → `Waiting for them to accept`. */
  sub: string
  /** Tick glyph when done: `✓` (pending `◔`) (§7.1). */
  tickGlyph: string
}

export function ticketAssignment(t: Ticket): TicketAssignment {
  const assigned = t.seats.filter(seatFilled).length
  const need = t.seats.length - assigned
  const done = need === 0
  const pending = t.seats.some((s) => s.status === 'pending')
  const mine = t.seats.every((s) => !!s.mine)
  const names = t.seats.filter(seatFilled).map(holderLabel)
  const sub = done
    ? pending
      ? 'Waiting for them to accept'
      : mine
        ? 'You'
        : names.length > 1
          ? `${names.length} people assigned`
          : names[0] ?? ''
    : need === t.seats.length
      ? `${need} to assign`
      : `${need} of ${t.seats.length} still to assign`
  return {
    assigned,
    seats: t.seats.length,
    done,
    pending,
    mine,
    dot: !done ? 'need' : pending ? 'pending' : 'done',
    sub,
    tickGlyph: pending ? '◔' : '✓',
  }
}

/** §7.2 header status pill: needs → `{k} of {n} assigned` (amber) / pending →
 *  `Invite pending` (blue) / mine → `✓ Your ticket` / done → `✓ Complete`. */
export function ticketStatusPill(t: Ticket): { text: string; dot: TicketDotState } {
  const a = ticketAssignment(t)
  const text = !a.done
    ? `${a.assigned} of ${a.seats} assigned`
    : a.pending
      ? 'Invite pending'
      : a.mine
        ? '✓ Your ticket'
        : '✓ Complete'
  return { text, dot: a.dot }
}

/** §7.2 mono suffix `· {n} people` / `· 1 person`. */
export function peopleLabel(t: Ticket): string {
  return t.seats.length > 1 ? `${t.seats.length} people` : '1 person'
}

// ---------------------------------------------------------------------------
// Order summary (canon §7.1: demo ⇒ `6 tickets · 3 need action · 3 complete ·
// 4 of 8 people assigned`, bar 50%)
// ---------------------------------------------------------------------------

export interface OrderSummary {
  ticketCount: number
  needTickets: number
  doneTickets: number
  assigned: number
  total: number
  pct: number
}

export function orderSummary(tickets: Ticket[]): OrderSummary {
  const seats = tickets.flatMap((t) => t.seats)
  const total = seats.length
  const assigned = seats.filter(seatFilled).length
  const needTickets = tickets.filter((t) => t.seats.some((s) => s.status === 'unassigned')).length
  const doneTickets = tickets.length - needTickets
  return {
    ticketCount: tickets.length,
    needTickets,
    doneTickets,
    assigned,
    total,
    pct: total === 0 ? 0 : Math.round((assigned / total) * 100),
  }
}

// ---------------------------------------------------------------------------
// Seat block (canon §7.2)
// ---------------------------------------------------------------------------

export interface SeatView {
  seatNo: number
  filled: boolean
  /** Roundel number (mono). */
  num: string
  /** Kicker `Person {i} of {n}` (single seat: `Person`). */
  personLabel: string
  isMine: boolean
  /** Holder line — name/email; empty state `No one assigned yet` (amber). */
  who: string
  /** Optional sub-line (§7.2): invited → `Invite sent{ to name} — waiting for
   *  them to accept`; child/minor variants are V1.1 seams (§12.2). */
  sub: string
  subTone: 'invited' | 'muted' | null
}

export function seatView(t: Ticket, s: Seat): SeatView {
  const filled = seatFilled(s)
  const invited = s.status === 'pending'
  // TODO(V1.1 kids §12.2): the child / minor sub-line variants —
  //   `On your account — you complete their details`,
  //   `Under 18 · you manage everything · waiver to {guardian}`,
  //   `Under 18 · own login ({email}) · you sign and pay` —
  // need the child/guardian record the kids amendment lands; today's holders
  // are self/invited-adult only, so only the invited sub-line can occur live.
  const sub = invited
    ? `Invite sent${s.holder?.name ? ' to ' + s.holder.name : ''} — waiting for them to accept`
    : s.holder?.kind === 'child'
      ? 'On your account — you complete their details'
      : ''
  return {
    seatNo: s.seatNo,
    filled,
    num: String(s.seatNo),
    personLabel: t.seats.length > 1 ? `Person ${s.seatNo} of ${t.seats.length}` : 'Person',
    isMine: !!s.mine,
    who: filled ? holderLabel(s) : 'No one assigned yet',
    sub,
    subTone: sub ? (invited ? 'invited' : 'muted') : null,
  }
}
